import { useState, useMemo } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import { FormHelperText, IconButton, MenuItem, Select, Typography } from "@mui/material";
import { chartColorList, monthsList, yearsList } from "../constants";
import {
    Tooltip,
    ResponsiveContainer,
    Legend,
    BarChart,
    XAxis,
    YAxis,
    Bar,
} from "recharts";
import { useAppDispatch } from "../../../hooks/StoreHooks";
import { fetchPresenceQuotaThunk } from "../../../store/thunk/reportingThunk";
import { usePresenceQuotaData } from "../../../store/selectors/reportingSelectors";
import { useTranslation } from "react-i18next";
import {
    OptionContainer,
    OptionRow,
    ActionRow,
    StyledFormControl,
    ChartContainer,
    NoDataText
} from "./Widget.styles";
import { NoDataContainer } from "../Dashboard.styles";
import { chartScoreType } from "./GeneralInfo/ChartDateType";
import { GeneralButton } from "../../../common/ui/GeneralButton";

const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
        return (
            <div style={{
                backgroundColor: "white",
                padding: "10px",
                border: "1px solid #ccc",
                borderRadius: "5px",
                boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)"
            }}>
                <Typography fontWeight="bold">{payload[0].payload.name}</Typography>
                <Typography>{`Quota: ${payload[0].value.toFixed(2)}`}</Typography>
            </div>
        );
    }
    return null;
};

const PresenceQuotaChart = () => {
    const { t } = useTranslation();
    const [year, setYear] = useState<string>(new Date().getFullYear().toString());
    const [month, setMonth] = useState<string>(monthsList[0]);
    const [loading, setLoading] = useState<boolean>(false);
    const [emptyChartData, setEmptyChartData] = useState<boolean>(true);
    const dispatch = useAppDispatch();
    const presenceQuotaData = usePresenceQuotaData();

    const handleSelection = (setter: React.Dispatch<React.SetStateAction<string>>) => (e: any) => setter(e.target.value);

    const fetchChartData = () => {
        setLoading(true);
        dispatch(fetchPresenceQuotaThunk({
            year: parseInt(year, 10),
            month: monthsList.indexOf(month) + 1,
        })).then(() => {
            setLoading(false);
            setEmptyChartData(presenceQuotaData.length === 0);
        });
    };

    const resetChartData = () => {
        setEmptyChartData(true);
    };

    const presenceQuotaChartData: chartScoreType[] = useMemo(() => (
        emptyChartData ?
            [{ name: "", score: 0, fill: "#cccccc" }] :
            presenceQuotaData.map((elem, i) => ({
                name: elem.actionModuleName,
                score: elem.averagePresenceQuota,
                fill: chartColorList[i % chartColorList.length],
            }))
    ), [presenceQuotaData, emptyChartData]);

    return (
        <>
            <OptionContainer>
                <OptionRow>
                    <StyledFormControl>
                        <Select value={year} onChange={handleSelection(setYear)} IconComponent={ExpandMoreIcon}>
                            {yearsList.map((item) => (
                                <MenuItem key={item} value={item}>{item}</MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>{t("dashboardPage.chooseYear")}</FormHelperText>
                    </StyledFormControl>

                    <StyledFormControl>
                        <Select value={month} onChange={handleSelection(setMonth)} IconComponent={ExpandMoreIcon}>
                            {monthsList.map((item) => (
                                <MenuItem key={item} value={item}>{item}</MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>{t("dashboardPage.chooseMonth")}</FormHelperText>
                    </StyledFormControl>

                    <IconButton
                        onClick={resetChartData}
                        aria-label={t("buttons.reset")}
                        sx={{ marginRight: 1, color: "#555" }}
                        size="small"
                    >
                        <RefreshIcon fontSize="small" />
                    </IconButton>
                </OptionRow>

                <ActionRow>
                    <GeneralButton
                        appVariant="widget"
                        onClick={fetchChartData}
                    >
                        {t("buttons.apply")}
                    </GeneralButton>
                </ActionRow>
            </OptionContainer>

            {loading || presenceQuotaChartData.length === 0 ? (
                <NoDataContainer>
                    <NoDataText>{t("dashboardPage.noDataAvailable")}</NoDataText>
                </NoDataContainer>
            ) : (
                <ChartContainer>
                    <ResponsiveContainer>
                        <BarChart data={presenceQuotaChartData} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <Bar dataKey="score" fill="#82ca9d" />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartContainer>
            )}
        </>
    );
};

export default PresenceQuotaChart;
